import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Droplets, Coins, Camera, Crown, Sparkles, PartyPopper, Star } from "lucide-react";
import { DAYS_PT, formatBRL, todayDow, todayISO } from "@/lib/api";
import CheckinDialog from "@/components/CheckinDialog";

const EMOJIS_HELLO = ["👋", "🌟", "🚀", "🎈", "🌈"];

export default function ChildDashboard() {
    const { user } = useAuth();
    const { children, activeChildId } = useApp();
    const [tasks, setTasks] = useState([]);
    const [checkins, setCheckins] = useState([]);
    const [bathStatus, setBathStatus] = useState(null);
    const [summary, setSummary] = useState({});
    const [checkinTask, setCheckinTask] = useState(null);
    const [helloEmoji] = useState(EMOJIS_HELLO[Math.floor(Math.random() * EMOJIS_HELLO.length)]);

    const dow = todayDow();
    const today = todayISO();
    const activeChild = children.find((c) => c.id === activeChildId) || user;

    const load = async () => {
        const [t, c, bs, s] = await Promise.all([
            api.get("/tasks"),
            api.get("/checkins", { params: { date: today } }),
            api.get("/bath/status"),
            api.get("/payments/summary"),
        ]);
        setTasks(t.data);
        setCheckins(c.data);
        setBathStatus(bs.data);
        setSummary(s.data || {});
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const todaysTasks = tasks.filter(
        (t) => t.active && (!t.recurring_days?.length || t.recurring_days.includes(dow))
    );
    const doneIds = new Set(checkins.map((c) => c.task_id));
    const pending = todaysTasks.filter((t) => !doneIds.has(t.id));
    const done = todaysTasks.filter((t) => doneIds.has(t.id));
    const pct = todaysTasks.length ? Math.round((done.length / todaysTasks.length) * 100) : 0;
    const earnings = summary[activeChild?.id] || { pending: 0, paid: 0 };

    const myBathEntry = bathStatus?.today_entries?.find((e) => e.child_id === activeChild?.id);
    const bathDone = !!myBathEntry;
    const bathIsFirst = myBathEntry?.is_first;
    const expectedId = bathStatus?.expected_first_child_id;
    const mySlotIsFirst = expectedId === activeChild?.id && !bathStatus?.today_first_child_id;

    const markBath = async () => {
        await api.post("/bath/checkins", { child_id: activeChild.id, date: today });
        load();
    };

    if (!activeChild) {
        return <Card className="p-10 border-[#E2DCD5] text-center"><p className="text-slate-600">Peça pro seu pai/mãe cadastrar você primeiro.</p></Card>;
    }

    return (
        <div className="space-y-7">
            {/* Hero / greeting */}
            <div className="relative overflow-hidden rounded-3xl border border-[#E2DCD5] bg-gradient-to-br from-white via-[#FFF8F1] to-[#FFF1EA] p-6 sm:p-8 rise">
                <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-20" style={{ backgroundColor: activeChild.avatar_color }} />
                <div className="absolute -left-6 -bottom-10 h-28 w-28 rounded-full opacity-10" style={{ backgroundColor: activeChild.avatar_color }} />
                <div className="relative flex items-center gap-4">
                    <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-3xl grid place-items-center text-white text-3xl font-bold shadow-lg" style={{ backgroundColor: activeChild.avatar_color }}>
                        {activeChild.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-widest text-[#8a7968]">
                            {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight" style={{ fontFamily: "Outfit" }}>
                            Oi, {activeChild.name}! <span>{helloEmoji}</span>
                        </h1>
                        <p className="text-slate-600 text-sm sm:text-base mt-0.5">
                            {pending.length === 0 ? "Fechou tudo por hoje — manda ver no banho!" : `Você tem ${pending.length} ${pending.length === 1 ? "missão" : "missões"} esperando.`}
                        </p>
                    </div>
                </div>
                <div className="relative mt-5">
                    <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
                        <span className="font-semibold">Progresso de hoje</span>
                        <span>{done.length}/{todaysTasks.length} · {pct}%</span>
                    </div>
                    <Progress value={pct} className="h-3 bg-white" />
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 rise rise-1">
                <Card className="p-4 border-sky-200 bg-sky-50/60" data-testid="stat-bath">
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-sky-600 text-white grid place-items-center">
                            <Droplets className="h-4 w-4" />
                        </div>
                        <div className="text-xs uppercase tracking-widest text-sky-800">Banho</div>
                    </div>
                    <div className="mt-2 text-base sm:text-lg font-bold text-sky-900" style={{ fontFamily: "Outfit" }}>
                        {bathDone ? (bathIsFirst ? "1º lugar ★" : "feito ✓") : mySlotIsFirst ? "Sua vez!" : "pendente"}
                    </div>
                    {!bathDone && (
                        <Button onClick={markBath} data-testid="mark-bath-done-button" size="sm" className="mt-2 bg-sky-600 hover:bg-sky-700 rounded-full w-full">
                            Tomei banho
                        </Button>
                    )}
                </Card>
                <Card className="p-4 border-amber-200 bg-amber-50/60" data-testid="stat-earnings">
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-amber-500 text-white grid place-items-center">
                            <Coins className="h-4 w-4" />
                        </div>
                        <div className="text-xs uppercase tracking-widest text-amber-800">Saldo</div>
                    </div>
                    <div className="mt-2 text-base sm:text-lg font-bold text-amber-900" style={{ fontFamily: "Outfit" }}>{formatBRL(earnings.pending)}</div>
                    <div className="text-[10px] text-amber-800/80 mt-0.5">A receber</div>
                </Card>
                <Card className="p-4 border-emerald-200 bg-emerald-50/60" data-testid="stat-streak">
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-emerald-600 text-white grid place-items-center">
                            <Star className="h-4 w-4" />
                        </div>
                        <div className="text-xs uppercase tracking-widest text-emerald-800">Feitas</div>
                    </div>
                    <div className="mt-2 text-base sm:text-lg font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>{done.length}</div>
                    <div className="text-[10px] text-emerald-800/80 mt-0.5">Hoje</div>
                </Card>
            </div>

            {/* Pending missions */}
            <section className="rise rise-2">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                        <Sparkles className="h-5 w-5 text-[#D96B43]" /> Missões de hoje
                    </h2>
                    <span className="text-xs text-slate-500">{pending.length} pendentes</span>
                </div>
                {pending.length === 0 ? (
                    <Card className="p-8 border-emerald-200 bg-emerald-50/40 text-center">
                        <PartyPopper className="h-10 w-10 mx-auto text-emerald-600" />
                        <p className="mt-3 text-emerald-900 font-bold text-lg" style={{ fontFamily: "Outfit" }}>Fechou tudo!</p>
                        <p className="text-sm text-emerald-700">Você é fera. Agora é só curtir o resto do dia.</p>
                    </Card>
                ) : (
                    <div className="grid gap-3">
                        {pending.map((t) => (
                            <Card
                                key={t.id}
                                className="p-4 border-[#E2DCD5] hover:shadow-md flex items-center gap-3 group transition"
                                data-testid={`pending-task-${t.id}`}
                            >
                                <div className="h-10 w-10 rounded-xl bg-[#FFF1EA] text-[#D96B43] grid place-items-center shrink-0">
                                    <Star className="h-5 w-5 group-hover:scale-110 transition-transform" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-slate-900 truncate">{t.title}</div>
                                    {t.description && <div className="text-xs text-slate-500 truncate">{t.description}</div>}
                                    <div className="flex gap-1.5 mt-1 flex-wrap">
                                        {t.reward_amount > 0 && (
                                            <span className="chip bg-amber-100 text-amber-900 border border-amber-200">
                                                <Coins className="h-3 w-3" /> {formatBRL(t.reward_amount)}
                                            </span>
                                        )}
                                        {t.photo_required && (
                                            <span className="chip bg-[#FFF1EA] text-[#C25933] border border-[#F2CBB6]">
                                                <Camera className="h-3 w-3" /> foto
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    onClick={() => setCheckinTask(t)}
                                    data-testid={`checkin-task-${t.id}`}
                                    className="bg-[#4A7C59] hover:bg-[#3a6549] text-white rounded-full shrink-0"
                                >
                                    <Camera className="h-4 w-4 mr-1.5" /> Fiz!
                                </Button>
                            </Card>
                        ))}
                    </div>
                )}
            </section>

            {done.length > 0 && (
                <section className="rise rise-3">
                    <h2 className="text-xl font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Já fiz hoje
                    </h2>
                    <div className="grid gap-3">
                        {done.map((t) => (
                            <Card key={t.id} className="p-3 border-emerald-200 bg-emerald-50/40 flex items-center gap-3" data-testid={`done-task-${t.id}`}>
                                <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                                <div className="flex-1 min-w-0 font-semibold text-slate-800 truncate">{t.title}</div>
                                {t.reward_amount > 0 && (
                                    <span className="chip bg-amber-100 text-amber-800">+{formatBRL(t.reward_amount)}</span>
                                )}
                            </Card>
                        ))}
                    </div>
                </section>
            )}

            <CheckinDialog
                open={!!checkinTask}
                onOpenChange={(v) => !v && setCheckinTask(null)}
                task={checkinTask}
                onDone={load}
            />
        </div>
    );
}
